import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { AxiosError } from 'axios'

import { axiosInstance } from '../helpers'
import {
    Concession,
    CreateConcessionInput,
    UpdateConcessionInput,
} from '../models/Concession'
import { useUser } from './useUser'

const CONCESSIONS_KEY = 'concessions'
const BASE = '/beta/api/concession'

/** Pull a human-readable message out of a DRF 400 error response. */
const extractError = (error: unknown, fallback: string): string => {
    const data = (error as AxiosError)?.response?.data as
        | Record<string, unknown>
        | undefined

    if (data) {
        const nonField = data.non_field_errors
        if (Array.isArray(nonField) && nonField.length > 0) {
            return String(nonField[0])
        }
        // Surface the first field error if present.
        const firstKey = Object.keys(data)[0]
        if (firstKey) {
            const val = data[firstKey]
            if (Array.isArray(val) && val.length > 0) {
                return `${firstKey}: ${val[0]}`
            }
            if (typeof val === 'string') {
                return val
            }
        }
    }

    return fallback
}

export const useConcessions = () => {
    const queryClient = useQueryClient()
    const { workspaceUser } = useUser()

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data: concessions, isLoading, error, refetch } = useQuery<
        Concession[],
        Error
    >({
        queryKey: [CONCESSIONS_KEY, apartmentId],
        queryFn: async () => {
            const res = await axiosInstance.get<Concession[]>(`${BASE}/`)
            return res.data
        },
        enabled: isApartment && !!apartmentId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: [CONCESSIONS_KEY] })

    const createConcession = useMutation({
        mutationFn: async (body: CreateConcessionInput) => {
            const res = await axiosInstance.post<Concession>(`${BASE}/`, body)
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Concession created')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to create concession'))
        },
    })

    const updateConcession = useMutation({
        mutationFn: async ({
            id,
            body,
        }: {
            id: number
            body: UpdateConcessionInput
        }) => {
            const res = await axiosInstance.patch<Concession>(
                `${BASE}/${id}/`,
                body,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Concession updated')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to update concession'))
        },
    })

    const toggleActive = useMutation({
        mutationFn: async ({
            id,
            isActive,
        }: {
            id: number
            isActive: boolean
        }) => {
            const res = await axiosInstance.patch<Concession>(
                `${BASE}/${id}/`,
                {
                    is_active: isActive,
                },
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to update concession'))
        },
    })

    const deleteConcession = useMutation({
        mutationFn: async (id: number) => {
            await axiosInstance.delete(`${BASE}/${id}/`)
        },
        onSuccess: () => {
            invalidate()
            toast.success('Concession deleted')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to delete concession'))
        },
    })

    return {
        concessions: concessions ?? [],
        isLoading,
        error,
        refetch,
        isApartment,
        createConcession,
        updateConcession,
        toggleActive,
        deleteConcession,
    }
}
